import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/css-utils'
import { CheckCircle2, CircleAlert, CircleX, Info } from 'lucide-react'

export type AlertVariant = 'default' | 'warning' | 'success' | 'error'
const alertVariants = cva(
  'relative w-full rounded-lg border px-4 py-3 text-sm' +
    '[&>svg+div]:translate-y-[-3px] [&>svg]:absolute' +
    '[&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground [&>svg~*]:pl-2 ' +
    'flex items-center',
  {
    variants: {
      variant: {
        default:
          'bg-alert-default-bg/5 border-alert-default-border text-copy-primary',
        warning:
          'bg-alert-warning-bg/5 border-alert-warning-border text-copy-primary',
        success:
          'bg-alert-success-bg/5 border-alert-success-border text-copy-primary',
        error:
          'bg-alert-error-bg/5 border-alert-error-border text-copy-primary',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> &
    VariantProps<typeof alertVariants> & { hideIcon?: boolean }
>(
  (
    { className, variant = 'default', children, hideIcon = false, ...props },
    ref,
  ) => (
    <div
      ref={ref}
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    >
      {!hideIcon && variant === 'warning' ? (
        <CircleAlert className="h-5 w-5 text-alert-warning-iconColor shrink-0" />
      ) : null}
      {!hideIcon && variant === 'default' ? (
        <Info className="h-5 w-5 text-alert-default-iconColor shrink-0" />
      ) : null}
      {!hideIcon && variant === 'success' ? (
        <CheckCircle2 className="h-5 w-5 text-alert-success-iconColor shrink-0" />
      ) : null}
      {!hideIcon && variant === 'error' ? (
        <CircleX className="h-5 w-5 text-alert-error-iconColor shrink-0" />
      ) : null}
      <div className="flex flex-col">{children}</div>
    </div>
  ),
)
Alert.displayName = 'Alert'

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn(
      'mb-1 font-medium text-sm leading-none tracking-tight',
      className,
    )}
    {...props}
  />
))
AlertTitle.displayName = 'AlertTitle'

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('text-sm [&_p]:leading-relaxed', className)}
    {...props}
  />
))
AlertDescription.displayName = 'AlertDescription'

export { Alert, AlertTitle, AlertDescription }
